import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";
import defaultImage from "../../../assets/images/default.jpg";

const INK = "#1A1614";
const MUTED = "#8A857F";
const HAIRLINE = "#E8E1D3";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const RED = "#DC2626";
const GREEN = "#16A34A";
const BLUE = "#2563EB";

// Creator marketplace oversight (read-only): brands hire creators for promo
// work, pay in coins; the platform keeps feeCoins. No admin mutations here.
const LIMIT = 50;

const TABS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "accepted", label: "Accepted" },
  { key: "completed", label: "Completed" },
  { key: "declined", label: "Declined" },
  { key: "cancelled", label: "Cancelled" },
];

const STATUS_STYLE = {
  pending: { bg: "#FBF3DC", fg: GOLD },
  accepted: { bg: "#E7EEFC", fg: BLUE },
  completed: { bg: "#E7F6EC", fg: GREEN },
  declined: { bg: "#FDECEC", fg: RED },
  cancelled: { bg: "#F1EEE8", fg: MUTED },
};

const CreatorHires = () => {
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(""); // hire _id with expanded brief

  const load = () =>
    apiInstanceFetch
      .get(`creatorMarket/admin/list?status=${status}&page=${page}&limit=${LIMIT}`)
      .then((r) => {
        if (r?.status) {
          setRows(r.data || []);
          setTotal(r.total || 0);
        }
      })
      .catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status, page]);

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  const brandName = (r) => {
    const u = r.brandUserId || {};
    return `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Unknown";
  };
  const creatorName = (r) => {
    const c = r.creatorAffiliateId || {};
    return `${c.firstName || ""} ${c.lastName || ""}`.trim() || "Unknown";
  };

  const badge = (s) => {
    const st = STATUS_STYLE[s] || STATUS_STYLE.cancelled;
    return (
      <span
        style={{
          display: "inline-block",
          padding: "3px 10px",
          borderRadius: 999,
          fontSize: 11.5,
          fontWeight: 700,
          background: st.bg,
          color: st.fg,
          textTransform: "capitalize",
        }}
      >
        {s}
      </span>
    );
  };

  return (
    <div className="mainCategory">
      <Title name="Creator Hires" />

      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8, margin: "16px 0" }}>
        <i className="fa-solid fa-handshake" style={{ color: GOLD }} />
        <span style={{ fontWeight: 700, color: INK, fontSize: 15 }}>
          {TABS.find((t) => t.key === status)?.label} ({total})
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {TABS.map((t) => (
            <button
              key={t.key || "all"}
              onClick={() => {
                setStatus(t.key);
                setPage(1);
              }}
              className="btn btn-sm"
              style={{
                border: `1px solid ${status === t.key ? INK : HAIRLINE}`,
                background: status === t.key ? INK : "#FFF",
                color: status === t.key ? "#FFF" : INK,
                fontWeight: 600,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, padding: 48, textAlign: "center", color: MUTED }}>
          <i className="fa-solid fa-handshake" style={{ fontSize: 34, color: HAIRLINE }} />
          <div style={{ marginTop: 12, fontWeight: 600 }}>No hire requests here</div>
        </div>
      ) : (
        <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, overflow: "auto" }}>
          <table className="table align-middle" style={{ margin: 0, minWidth: 980 }}>
            <thead>
              <tr style={{ background: CREAM }}>
                {["Date", "Brand", "Creator", "Brief", "Budget", "Platform Fee", "Status"].map((h) => (
                  <th key={h} style={{ fontSize: 11, textTransform: "uppercase", color: MUTED, padding: "10px 12px", borderBottom: `2px solid ${HAIRLINE}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id}>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    <div>{dayjs(r.createdAt).format("DD MMM YYYY")}</div>
                    {r.completedAt ? (
                      <div style={{ fontSize: 11, color: GREEN }}>done {dayjs(r.completedAt).format("DD MMM")}</div>
                    ) : r.respondedAt ? (
                      <div style={{ fontSize: 11, color: MUTED }}>replied {dayjs(r.respondedAt).format("DD MMM")}</div>
                    ) : null}
                  </td>
                  <td style={{ padding: "8px 12px", maxWidth: 200 }}>
                    <div style={{ fontWeight: 600, color: INK }}>{brandName(r)}</div>
                    <div style={{ fontSize: 11.5, color: MUTED, wordBreak: "break-all" }}>{r.brandUserId?.email || ""}</div>
                  </td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <img
                        src={r.creatorAffiliateId?.image || defaultImage}
                        alt=""
                        style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", border: `1px solid ${HAIRLINE}` }}
                        onError={(e) => (e.currentTarget.src = defaultImage)}
                      />
                      <span style={{ fontWeight: 600, color: INK }}>{creatorName(r)}</span>
                    </div>
                  </td>
                  <td
                    style={{ padding: "8px 12px", maxWidth: 300, cursor: "pointer" }}
                    title={expanded === r._id ? "Click to collapse" : "Click to expand"}
                    onClick={() => setExpanded(expanded === r._id ? "" : r._id)}
                  >
                    <div
                      style={
                        expanded === r._id
                          ? { whiteSpace: "pre-wrap", color: INK }
                          : {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              color: INK,
                            }
                      }
                    >
                      {r.brief || <span style={{ color: MUTED }}>—</span>}
                    </div>
                  </td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{(r.budgetCoins || 0).toLocaleString()} 🪙</td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{(r.feeCoins || 0).toLocaleString()} 🪙</td>
                  <td style={{ padding: "8px 12px" }}>{badge(r.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 ? (
        <div className="d-flex align-items-center justify-content-end" style={{ gap: 10, marginTop: 14 }}>
          <button
            className="btn btn-sm"
            style={{ border: `1px solid ${HAIRLINE}`, background: "#fff", color: INK, fontWeight: 600 }}
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Prev
          </button>
          <span style={{ fontSize: 13, color: MUTED }}>
            Page {page} of {pages}
          </span>
          <button
            className="btn btn-sm"
            style={{ border: `1px solid ${HAIRLINE}`, background: "#fff", color: INK, fontWeight: 600 }}
            disabled={page >= pages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default CreatorHires;
